import { DAILY_RETURN_BONUS, FIRST_GAME_BONUS } from './levels.js';
/**
 * ─── THE LEDGER: one scoring currency for every Ringmaster game ───────────────
 *
 * Every game awards points on the SAME scale so a room can switch games without
 * the leaderboard changing meaning. Totals live on the ROOM (server-side), not
 * inside an engine's state — that is what makes them survive `switchGame`.
 *
 * THE INVARIANT (asserted in tests, do not weaken):
 *
 *     any winner's game total  >=  WIN_POINTS  >  BONUS_CAP  >=  any loser's total
 *
 * It holds by construction: a winner always banks `WIN_POINTS`, and every
 * player's BONUS awards are clamped to `BONUS_CAP` per game. So a winner can
 * never finish a single game below a loser. (Across a whole session the
 * cumulative leaderboard may of course rank a loser above a winner.)
 *
 * Awards are computed inside the engines (pure, deterministic) but reach the
 * wire EXACTLY ONCE, at END, via `Effect.awards` → the server's room totals →
 * `PublicView.awards`. They must NEVER surface mid-game: an award code is a role
 * tell (a `SCENT` in round 1 names a wolf), which is why the e2e secret-leak
 * sweep asserts `awards` is null before END.
 */
/** Points for being on the winning team. Same in every game, every team. */
export const WIN_POINTS = 200;
/** Hard clamp on the SUM of a single player's bonus awards in a single game. */
export const BONUS_CAP = 150;
export const AWARD_CODES = [
    // ── Universal ──────────────────────────────────────────────────────────────
    'WIN',
    'FIRST_CURTAIN',
    'ENCORE',
    // ── Blood Moon ─────────────────────────────────────────────────────────────
    'SCENT',
    'GOOD_HUNT',
    'SEER_MARK',
    'ORACLE',
    'MARTYR',
    'LONE_WOLF',
    'SILVER_TONGUE',
    'CLEAN_KILL',
    'BETRAYAL',
    'BETRAYAL_LYNCH',
    // ── Blood Moon: One Night (GOOD_HUNT / SEER_MARK / MARTYR / BETRAYAL shared) ─
    'STOLEN_FANGS',
    'JOKERS_MARK',
    'INNOCENT_FRAME',
    'CURTAIN_CALL',
    // ── The Whisper ────────────────────────────────────────────────────────────
    'FINDER',
    'SWIFT',
    'RIGHT_CALL',
    'PERFECT_CRIME',
];
const BM = ['bloodmoon'];
const ON = ['onenight'];
const BM_ON = ['bloodmoon', 'onenight'];
const WH = ['whisper'];
export const AWARD_META = {
    WIN: {
        code: 'WIN',
        label: 'Victory',
        description: 'Your side took the night.',
        points: WIN_POINTS,
        cap: null,
        games: ['bloodmoon', 'onenight', 'whisper'],
    },
    /**
     * THE TWO AWARDS NO ENGINE MINTS — a welcome, and a welcome BACK.
     *
     * Both are DB state (`xp_awards.kind`), which an engine is pure and cannot
     * know: FIRST_CURTAIN is a player's first completed match EVER, ENCORE their
     * first of a new day (`shared/levels.ts DAILY_BONUS_GAP_HOURS` — a gap, not a
     * calendar date). The server decides which, if either, is owed at match start
     * and freezes it on `PlayerPublic.bonus`; the client appends the matching row
     * so the grant lands as a real ceremony row with its own burst.
     *
     * Mechanically identical and mutually exclusive — a first game ever is also a
     * first game today, and FIRST_CURTAIN wins that tie because you can only be
     * new once. Neither reaches `Effect.awards`, so neither enters the room's
     * cumulative `scores`: the room leaderboard stays pure game points.
     */
    FIRST_CURTAIN: {
        code: 'FIRST_CURTAIN',
        label: 'First Curtain',
        description: 'Your first game. The Ringmaster remembers you now.',
        points: FIRST_GAME_BONUS,
        cap: null,
        games: ['bloodmoon', 'onenight', 'whisper'],
    },
    ENCORE: {
        code: 'ENCORE',
        label: 'Encore',
        description: 'Your first game today. The Ringmaster kept your seat.',
        points: DAILY_RETURN_BONUS,
        cap: null,
        games: ['bloodmoon', 'onenight', 'whisper'],
    },
    // ── Blood Moon ─────────────────────────────────────────────────────────────
    SCENT: {
        code: 'SCENT',
        label: 'Scent of Blood',
        description: 'You put your vote on a werewolf.',
        points: 30,
        cap: 90,
        games: BM,
    },
    GOOD_HUNT: {
        code: 'GOOD_HUNT',
        label: 'Good Hunt',
        description: 'Your vote landed on a player holding a Werewolf card at dawn.',
        points: 40,
        cap: 80,
        games: BM_ON,
    },
    SEER_MARK: {
        code: 'SEER_MARK',
        label: "Seer's Mark",
        description: 'You looked a werewolf in the eye and knew it.',
        points: 20,
        cap: 40,
        games: BM_ON,
    },
    ORACLE: {
        code: 'ORACLE',
        label: 'The Oracle Lives',
        description: 'You survived the night as Seer and the village won.',
        points: 80,
        cap: null,
        games: BM,
    },
    MARTYR: {
        code: 'MARTYR',
        label: 'Martyr',
        description: 'You died for the village.',
        points: 30,
        cap: null,
        games: BM_ON,
    },
    LONE_WOLF: {
        code: 'LONE_WOLF',
        label: 'Lone Wolf',
        description: 'You hunted alone — and the pack still won.',
        points: 60,
        cap: null,
        games: BM,
    },
    SILVER_TONGUE: {
        code: 'SILVER_TONGUE',
        label: 'Silver Tongue',
        description: 'The village lynched an innocent you voted for.',
        points: 30,
        cap: 90,
        games: BM,
    },
    CLEAN_KILL: {
        code: 'CLEAN_KILL',
        label: 'Clean Kill',
        description: 'Your pack took the Seer in the night.',
        points: 30,
        cap: null,
        games: BM,
    },
    BETRAYAL: {
        code: 'BETRAYAL',
        label: 'Betrayal',
        description: 'You voted against your own packmate — and still won.',
        points: 40,
        cap: null,
        games: BM_ON,
    },
    BETRAYAL_LYNCH: {
        code: 'BETRAYAL_LYNCH',
        label: 'Blood Betrayal',
        description: 'You fed your own packmate to the mob — and still won.',
        points: 40,
        cap: null,
        games: BM,
    },
    // ── One Night ──────────────────────────────────────────────────────────────
    STOLEN_FANGS: {
        code: 'STOLEN_FANGS',
        label: 'Stolen Fangs',
        description: 'You stole the Werewolf card and won with the pack.',
        points: 60,
        cap: null,
        games: ON,
    },
    JOKERS_MARK: {
        code: 'JOKERS_MARK',
        label: "Joker's Mark",
        description: 'A player whose card you moved drew the most votes.',
        points: 40,
        cap: null,
        games: ON,
    },
    INNOCENT_FRAME: {
        code: 'INNOCENT_FRAME',
        label: 'Innocent Framed',
        description: 'You voted for a villager and the table killed them.',
        points: 20,
        cap: null,
        games: ON,
    },
    /**
     * Consolation for losing a lobby's FIRST One Night game (engine-gated on
     * `dealsDone === 1`) — a first-game loss is the moment most likely to lose the
     * whole group, so the ledger softens it and the WIN narration sells the
     * rematch. Every seat on the losing team banks it, once.
     */
    CURTAIN_CALL: {
        code: 'CURTAIN_CALL',
        label: 'Curtain Call',
        description: "Stood through your guild's first night and lost — the stage remembers.",
        points: 50,
        cap: 50,
        games: ON,
    },
    // ── The Whisper ────────────────────────────────────────────────────────────
    FINDER: {
        code: 'FINDER',
        label: 'The Word',
        description: 'You said the word first.',
        points: 80,
        cap: null,
        games: WH,
    },
    SWIFT: {
        code: 'SWIFT',
        label: 'Before the Wax Ran',
        description: 'You found it with candle to spare.',
        points: 1,
        cap: 100,
        games: WH,
    },
    RIGHT_CALL: {
        code: 'RIGHT_CALL',
        label: 'Right Call',
        description: 'You pointed at a deceiver and the hunt caught them.',
        points: 60,
        cap: null,
        games: WH,
    },
    PERFECT_CRIME: {
        code: 'PERFECT_CRIME',
        label: 'Perfect Crime',
        description: 'The candle died and the hunt never found you.',
        points: 60,
        cap: null,
        games: WH,
    },
};
/**
 * Codes that are LIFETIME XP ONLY — they never counted toward the room's game
 * score and are not bonuses, so they are outside `BONUS_CAP` entirely.
 *
 * Two consumers, and both were wrong before this existed:
 *  - the ceremony's "bonus capped" notice, which summed every non-WIN row and so
 *    accused a player of hitting a cap their actual bonuses were nowhere near;
 *  - the ledger's divider, which is what tells a player that these rows buy a
 *    level and not a place on tonight's leaderboard.
 */
export function isXpOnly(code) {
    return code === 'FIRST_CURTAIN' || code === 'ENCORE';
}
/**
 * Clamp one player's awards to the invariant: the WIN row passes through
 * untouched, the bonus rows are trimmed (in order, last row shaved) so their sum
 * never exceeds `BONUS_CAP`. Pure — engines call it as the last step before
 * emitting, so what reaches the wire is already the truth the ledger shows.
 */
export function clampAwards(awards) {
    const out = [];
    let bonus = 0;
    for (const a of awards) {
        // WIN is the floor of the invariant; FIRST_CURTAIN and ENCORE are not game
        // bonuses at all (they are profile-layer grants no engine mints). None may
        // be shaved — clamping any of them would break the very invariant this
        // function enforces, and would silently undo a grant sized to guarantee a
        // level-up.
        if (a.code === 'WIN' || a.code === 'FIRST_CURTAIN' || a.code === 'ENCORE') {
            out.push(a);
            continue;
        }
        if (bonus >= BONUS_CAP)
            continue;
        const points = Math.min(a.points, BONUS_CAP - bonus);
        if (points <= 0)
            continue;
        bonus += points;
        out.push(points === a.points ? a : { ...a, points });
    }
    return out;
}
/** Total of an award list (what the player banks this game). */
export function totalOf(awards) {
    return awards.reduce((sum, a) => sum + a.points, 0);
}
//# sourceMappingURL=scoring.js.map